import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Repeat, Home, Search, User, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { COMUNI, TIPOLOGIE } from "@/data/properties";
import useSEO from "@/hooks/useSEO";

const selectCls = "w-full h-12 rounded-xl border border-border-subtle bg-white px-3 text-sm outline-none focus:border-brand";
const fld = "bg-white border-border-subtle focus-visible:ring-brand/30 focus-visible:border-brand rounded-xl h-12";

const STEPS = [
  { key: "attuale", label: "Il tuo immobile", icon: Home },
  { key: "cerca", label: "Cosa cerchi", icon: Search },
  { key: "contatti", label: "Contatti", icon: User },
];

export default function CambioCasa() {
  useSEO({ title: "Cambio Casa — Vendi e Compra Senza Pensieri | Casamore Group", description: "Vuoi cambiare casa? Casamore Group ti accompagna nella vendita del tuo immobile e nell'acquisto della nuova casa, coordinando i tempi in un unico percorso.", canonical: window.location.origin + "/cambio-casa" });

  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    attTipo: "", attComune: "", attZona: "", attSuperficie: "", attValore: "",
    cercaTipo: "", cercaComune: "", cercaBudget: "", cercaNote: "",
    nome: "", telefono: "", email: "", consenso: false,
  });
  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const next = () => {
    if (step === 0 && !data.attTipo) { toast.error("Indica il tipo di immobile che possiedi."); return; }
    if (step === 1 && !data.cercaTipo) { toast.error("Indica cosa stai cercando."); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.nome || !data.telefono) { toast.error("Inserisci nome e telefono."); return; }
    if (!data.consenso) { toast.error("Accetta l'informativa privacy."); return; }
    try {
      await api.post("/public/leads", {
        tipo: "cambio_casa",
        contatto: { nome: data.nome, telefono: data.telefono, email: data.email || null },
        payload: {
          "Immobile attuale": [data.attTipo, data.attComune, data.attZona].filter(Boolean).join(" · "),
          "Superficie attuale": data.attSuperficie ? `${data.attSuperficie} mq` : "—",
          "Valore stimato": data.attValore ? `€${data.attValore}` : "Da valutare",
          "Cerca": [data.cercaTipo, data.cercaComune].filter(Boolean).join(" · "),
          "Budget acquisto": data.cercaBudget ? `€${data.cercaBudget}` : "—",
          "Note": data.cercaNote || "—",
        },
        consensoPrivacy: true,
        provenienza: "Cambio Casa",
      });
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 429) toast.error("Troppe richieste, riprova tra poco.");
      else toast.error("Invio non riuscito, riprova.");
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-32 min-h-[70vh]">
        <div className="mx-auto max-w-lg px-6 text-center" data-testid="cambio-success">
          <span className="grid place-items-center h-16 w-16 rounded-full bg-brand text-white mx-auto"><Check className="h-8 w-8" /></span>
          <h1 className="font-display text-4xl font-semibold text-anthracite mt-6">Richiesta inviata!</h1>
          <p className="mt-3 text-anthracite/70">Analizzeremo la tua situazione e ti proporremo un piano su misura per vendere e comprare senza pensieri. Ti ricontatteremo a breve.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24">
      <div className="mx-auto max-w-2xl px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-brand">
            <Repeat className="h-4 w-4" /> Cambio Casa
          </span>
          <h1 className="font-display font-semibold text-anthracite text-5xl sm:text-6xl tracking-tight mt-4 leading-[0.95]">
            Vendi e compra <span className="italic text-brand">senza pensieri</span>
          </h1>
          <p className="mt-4 text-lg text-anthracite/70">
            Ti accompagniamo in un unico percorso: valutiamo il tuo immobile e troviamo la casa che desideri, coordinando i tempi.
          </p>
        </motion.div>

        {/* Stepper */}
        <div className="mt-10 flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <span className={`grid place-items-center h-11 w-11 rounded-full border-2 transition-all ${i <= step ? "bg-brand text-white border-brand" : "bg-white text-anthracite/40 border-border-subtle"}`}>
                  {i < step ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                </span>
                <span className={`mt-2 text-xs font-semibold ${i <= step ? "text-brand" : "text-anthracite/40"}`}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-brand" : "bg-border-subtle"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-3xl border border-border-subtle bg-white p-6 sm:p-8">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4" data-testid="cambio-step-0">
                <label className="block"><span className="text-xs font-semibold text-anthracite/60">Tipologia immobile attuale *</span>
                  <select className={`${selectCls} mt-1`} value={data.attTipo} onChange={(e) => set({ attTipo: e.target.value })} data-testid="cambio-att-tipo">
                    <option value="">Seleziona</option>{TIPOLOGIE.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold text-anthracite/60">Comune</span>
                    <select className={`${selectCls} mt-1`} value={data.attComune} onChange={(e) => set({ attComune: e.target.value })} data-testid="cambio-att-comune">
                      <option value="">Seleziona</option>{COMUNI.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <label className="block"><span className="text-xs font-semibold text-anthracite/60">Zona</span>
                    <Input className={`${fld} mt-1`} placeholder="Es. Centro" value={data.attZona} onChange={(e) => set({ attZona: e.target.value })} data-testid="cambio-att-zona" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold text-anthracite/60">Superficie (mq)</span>
                    <Input className={`${fld} mt-1`} inputMode="numeric" placeholder="Es. 90" value={data.attSuperficie} onChange={(e) => set({ attSuperficie: e.target.value.replace(/\D/g, "") })} data-testid="cambio-att-superficie" />
                  </label>
                  <label className="block"><span className="text-xs font-semibold text-anthracite/60">Valore stimato (€)</span>
                    <Input className={`${fld} mt-1`} inputMode="numeric" placeholder="Facoltativo" value={data.attValore} onChange={(e) => set({ attValore: e.target.value.replace(/\D/g, "") })} data-testid="cambio-att-valore" />
                  </label>
                </div>
              </motion.div>
            )}
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4" data-testid="cambio-step-1">
                <label className="block"><span className="text-xs font-semibold text-anthracite/60">Cosa cerchi *</span>
                  <select className={`${selectCls} mt-1`} value={data.cercaTipo} onChange={(e) => set({ cercaTipo: e.target.value })} data-testid="cambio-cerca-tipo">
                    <option value="">Seleziona</option>{TIPOLOGIE.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs font-semibold text-anthracite/60">Zona preferita</span>
                    <select className={`${selectCls} mt-1`} value={data.cercaComune} onChange={(e) => set({ cercaComune: e.target.value })} data-testid="cambio-cerca-comune">
                      <option value="">Indifferente</option>{COMUNI.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                  <label className="block"><span className="text-xs font-semibold text-anthracite/60">Budget (€)</span>
                    <Input className={`${fld} mt-1`} inputMode="numeric" placeholder="Es. 250000" value={data.cercaBudget} onChange={(e) => set({ cercaBudget: e.target.value.replace(/\D/g, "") })} data-testid="cambio-cerca-budget" />
                  </label>
                </div>
                <label className="block"><span className="text-xs font-semibold text-anthracite/60">Note / esigenze</span>
                  <textarea className="w-full mt-1 rounded-xl border border-border-subtle bg-white p-3 text-sm outline-none focus:border-brand min-h-[90px]" placeholder="Es. cerco spazio esterno, terzo piano max…" value={data.cercaNote} onChange={(e) => set({ cercaNote: e.target.value })} data-testid="cambio-cerca-note" />
                </label>
              </motion.div>
            )}
            {step === 2 && (
              <motion.form key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} onSubmit={submit} className="space-y-3" data-testid="cambio-step-2">
                <Input className={fld} placeholder="Nome e cognome" value={data.nome} onChange={(e) => set({ nome: e.target.value })} data-testid="cambio-nome" />
                <Input className={fld} placeholder="Telefono" value={data.telefono} onChange={(e) => set({ telefono: e.target.value })} data-testid="cambio-telefono" />
                <Input className={fld} type="email" placeholder="Email (facoltativa)" value={data.email} onChange={(e) => set({ email: e.target.value })} data-testid="cambio-email" />
                <label className="flex items-start gap-2 text-xs text-anthracite/60">
                  <input type="checkbox" checked={data.consenso} onChange={(e) => set({ consenso: e.target.checked })} className="mt-0.5 accent-[#0F5C4A]" data-testid="cambio-privacy" />
                  Acconsento al trattamento dei dati secondo l'informativa privacy.
                </label>
                <button type="submit" className="w-full rounded-full bg-brand text-white font-semibold py-4 hover:bg-brand-hover transition-colors" data-testid="cambio-submit">Invia richiesta</button>
              </motion.form>
            )}
          </AnimatePresence>

          {step < 2 && (
            <div className="mt-6 flex items-center justify-between">
              <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-1.5 text-sm font-semibold text-anthracite/60 disabled:opacity-30 hover:text-brand transition-colors" data-testid="cambio-back">
                <ArrowLeft className="h-4 w-4" /> Indietro
              </button>
              <button onClick={next} className="group inline-flex items-center gap-2 rounded-full bg-brand text-white font-semibold px-6 py-3 hover:bg-brand-hover transition-colors" data-testid="cambio-next">
                Continua <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
